import { type PopoverOrigin } from "@mui/material";

import {
  ARROW_PROTRUSION_PX,
  ARROW_TIP_FROM_CORNER_PX,
  PORTALED_OVERLAY_SELECTOR,
  VIEWPORT_PADDING_PX,
} from "ui/popover/popover.constants";
import { type PopoverPlacement, type PopoverSide } from "ui/popover/popover.types";

export const PLACEMENT_ORIGINS: Record<
  PopoverPlacement,
  { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin }
> = {
  bottom: {
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
    transformOrigin: { vertical: "top", horizontal: "center" },
  },
  "bottom-start": {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  },
  "bottom-end": {
    anchorOrigin: { vertical: "bottom", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "right" },
  },
  top: {
    anchorOrigin: { vertical: "top", horizontal: "center" },
    transformOrigin: { vertical: "bottom", horizontal: "center" },
  },
  "top-start": {
    anchorOrigin: { vertical: "top", horizontal: "left" },
    transformOrigin: { vertical: "bottom", horizontal: "left" },
  },
  "top-end": {
    anchorOrigin: { vertical: "top", horizontal: "right" },
    transformOrigin: { vertical: "bottom", horizontal: "right" },
  },
  left: {
    anchorOrigin: { vertical: "center", horizontal: "left" },
    transformOrigin: { vertical: "center", horizontal: "right" },
  },
  "left-start": {
    anchorOrigin: { vertical: "top", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "right" },
  },
  "left-end": {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "bottom", horizontal: "right" },
  },
  right: {
    anchorOrigin: { vertical: "center", horizontal: "right" },
    transformOrigin: { vertical: "center", horizontal: "left" },
  },
  "right-start": {
    anchorOrigin: { vertical: "top", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  },
  "right-end": {
    anchorOrigin: { vertical: "bottom", horizontal: "right" },
    transformOrigin: { vertical: "bottom", horizontal: "left" },
  },
};

const PLACEMENT_SIDES: PopoverSide[] = ["top", "right", "bottom", "left"];

/** The side of the trigger the paper opens on. */
export const getPlacementSide = (placement: PopoverPlacement): PopoverSide =>
  PLACEMENT_SIDES.find((side) => placement.startsWith(side)) ?? "bottom";

export const isTopPlacement = (placement: PopoverPlacement) => getPlacementSide(placement) === "top";

/** Left and right placements open beside the trigger, so the paper's cross axis is vertical. */
export const isSidePlacement = (placement: PopoverPlacement) => {
  const side = getPlacementSide(placement);

  return side === "left" || side === "right";
};

/**
 * A `-start` / `-end` paper lines its edge up with the trigger's, which leaves the arrow wherever its
 * corner puts it. This is the slide along that edge that lands the arrow's tip on the trigger's centre.
 * Positive is right or down, whichever end the paper is aligned to — Popper's skidding convention.
 */
export const getArrowSkidding = (
  placement: string,
  trigger: { width: number; height: number },
): number => {
  const vertical = placement.startsWith("left") || placement.startsWith("right");
  const triggerHalf = (vertical ? trigger.height : trigger.width) / 2;

  if (placement.endsWith("-start")) return triggerHalf - ARROW_TIP_FROM_CORNER_PX;
  if (placement.endsWith("-end")) return ARROW_TIP_FROM_CORNER_PX - triggerHalf;

  return 0;
};

/**
 * The trigger-to-paper distance. `gutter` is the gap a reader sees, so with an arrow it is measured
 * from the arrow's tip — without that the tip, which reaches further than the default 8px gutter,
 * touches the trigger.
 */
export const getPanelOffset = (gutter: number, arrow: boolean) =>
  gutter + (arrow ? ARROW_PROTRUSION_PX : 0);

/** The slice of Popper's `offset` modifier argument the skidding reads. */
type OffsetContext = { placement: string; reference: { width: number; height: number } };

/**
 * `flip` stays off so the painted side always matches `placement`, which is what the arrow is drawn
 * from — and it matches `Popover`, which clamps into the viewport but never flips. `preventOverflow` on
 * both axes is that clamp. The skidding is a function because it reads the trigger's live size.
 */
export const getPopperModifiers = (offset: number, arrow: boolean) => [
  {
    name: "offset",
    options: {
      offset: ({ placement, reference }: OffsetContext) => [
        arrow ? getArrowSkidding(placement, reference) : 0,
        offset,
      ],
    },
  },
  { name: "flip", enabled: false },
  {
    name: "preventOverflow",
    options: { mainAxis: true, altAxis: true, padding: VIEWPORT_PADDING_PX },
  },
];

/**
 * A portaled descendant — a `Select`/`Autocomplete` listbox, a `Menu` — renders under
 * `document.body`, so containment alone reads a click on it as "outside": the panel would close on
 * mousedown and unmount before the option's own mouseup, and the choice would never register. ARIA
 * carries the ownership the DOM tree lost, because the control inside the Paper points at the
 * overlay's `id`. Scoped to overlays *this* panel claims — not to any MUI overlay on the page.
 */
export const isOverlayOwnedByPanel = (panel: HTMLElement | null, target: Node) => {
  if (!panel || !(target instanceof Element)) return false;

  const overlay = target.closest(PORTALED_OVERLAY_SELECTOR);
  if (!overlay?.id) return false;

  const id = CSS.escape(overlay.id);
  return Boolean(panel.querySelector(`[aria-controls="${id}"], [aria-owns="${id}"]`));
};
